import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GalleryUploader } from "@/components/ImageUploader";
import { BrandProductImages } from "@/components/BrandProductImages";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, UploadCloud, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, Package, Images, Share2, Link as LinkIcon } from "lucide-react";

const STATUS_COLORS = {
  "Submitted": "bg-sage/20 text-forest",
  "Under Review": "bg-mustard/25 text-earth",
  "Approved": "bg-sage/40 text-forest",
  "Not Currently Selected": "bg-brown/10 text-brown/70",
  "Changes Required": "bg-terracotta/15 text-terracotta",
  "Showcase Requested": "bg-terracotta/10 text-terracotta",
  "Showcased": "bg-terracotta/25 text-terracotta",
  "Live": "bg-emerald-100 text-emerald-700",
  "Out of Stock": "bg-mustard/20 text-earth",
  "Compliance Hold": "bg-red-100 text-red-700",
  "Inactive": "bg-brown/10 text-brown/50",
  "Archived": "bg-brown/15 text-brown/50",
};

const BLANK = { name: "", sku: "", category_name: "", mrp: "", price: "", pack_size: "", stock: 0, short_description: "", description: "", images: [] };
const TEMPLATE = "name,sku,category,mrp,price,pack_size,stock,short_description,description,images\nOrganic Almonds,BR-1001,Dry Fruits & Nuts,599,499,500g,50,Premium almonds,Full description,https://img1|https://img2";

function StatusBadge({ status }) {
  const s = status || "Submitted";
  return <Badge className={`${STATUS_COLORS[s] || "bg-sand text-brown"} hover:${STATUS_COLORS[s] || "bg-sand"}`} data-testid="product-status-badge">{s}</Badge>;
}

export function BrandCatalogue({ onReload, canEdit = true }) {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imagesFor, setImagesFor] = useState(null);
  const [selected, setSelected] = useState([]);
  const fileRef = useRef(null);

  const canShare = (s) => ["Approved", "Showcased", "Live"].includes(s);
  const shareLink = (p) => `${window.location.origin}/share/p/${p.id}`;
  const waMessage = (p) => {
    const feat = (p.wellness_needs && p.wellness_needs[0]) || (p.consumer_intents && p.consumer_intents[0]) || "Wellness";
    return `*${p.name}* by ${p.brand || ""}\n\n${p.short_description || ""}\n\n💰 ₹${p.price}\n🌿 ${feat}\n\n${shareLink(p)}`;
  };
  const shareWhatsApp = (p) => window.open(`https://wa.me/?text=${encodeURIComponent(waMessage(p))}`, "_blank");
  const copyLink = (p) => { navigator.clipboard.writeText(shareLink(p)); toast.success("Product link copied"); };
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const shareCollection = async (mode) => {
    try {
      const { data } = await api.post("/brand/collections", { product_ids: selected });
      const url = `${window.location.origin}${data.share_path}`;
      if (mode === "wa") window.open(`https://wa.me/?text=${encodeURIComponent(`Discover our LIVENEAT collection (${data.count} products):\n${url}`)}`, "_blank");
      else { navigator.clipboard.writeText(url); toast.success(`Collection link copied (${data.count} products)`); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create collection"); }
  };

  const load = () => api.get("/brand/products").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const reloadAll = () => { load(); onReload && onReload(); };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Product name is required");
    if (!editing.category_name.trim()) return toast.error("Category is required");
    if (!editing.mrp || Number(editing.mrp) <= 0) return toast.error("A valid MRP is required");
    setSaving(true);
    const payload = { ...editing, mrp: Number(editing.mrp) || 0, price: Number(editing.price) || 0, stock: Number(editing.stock) || 0 };
    try {
      if (editing.id) {
        const { data } = await api.put(`/brand/products/${editing.id}`, payload);
        toast.success(data.message || `Product saved (${data.status})`);
      } else {
        await api.post("/brand/products", payload);
        toast.success("Product added — status: Submitted");
      }
      setEditing(null); reloadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const runImport = async (mode) => {
    if (!file) return toast.error("Choose a CSV or Excel file first");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/brand/products/import?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (mode === "commit" && data.committed) {
        const r = data.result;
        toast.success(`Imported: ${r.added} new, ${r.updated} updated, ${r.flagged} flagged for review, ${r.discontinued} discontinued`);
        setImportOpen(false); setFile(null); setValidation(null); reloadAll();
      } else {
        setValidation(data);
        if (data.valid) toast.success("File validated — no errors. Review the summary and import.");
        else toast.error(`${data.errors.length} validation issue(s) found. Fix them before importing.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "liveneat-catalogue-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-2xl soft-shadow p-5" data-testid="brand-catalogue">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="font-heading text-xl text-forest">Product Catalogue</h2>
        <div className="flex gap-2">
          {canEdit && <>
          <Button variant="outline" onClick={() => { setImportOpen(true); setValidation(null); setFile(null); }} className="rounded-full border-sage" data-testid="open-import-btn"><UploadCloud className="w-4 h-4 mr-1" /> Upload CSV / Excel</Button>
          <Button onClick={() => setEditing({ ...BLANK })} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
          </>}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap bg-sage/15 border border-sage/40 rounded-xl px-4 py-2.5 mb-3" data-testid="share-bar">
          <span className="text-sm text-forest font-500">{selected.length} selected</span>
          <Button size="sm" onClick={() => shareCollection("wa")} className="rounded-full bg-forest hover:bg-earth text-cream h-8" data-testid="share-collection-wa"><Share2 className="w-3.5 h-3.5 mr-1" /> Share collection on WhatsApp</Button>
          <Button size="sm" variant="outline" onClick={() => shareCollection("copy")} className="rounded-full border-sage h-8" data-testid="share-collection-copy"><LinkIcon className="w-3.5 h-3.5 mr-1" /> Copy collection link</Button>
          <button onClick={() => setSelected([])} className="text-xs text-brown/60 hover:text-forest">Clear</button>
        </div>
      )}

      {!list ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> :
        list.length === 0 ? <p className="text-sm text-brown/60 py-8 text-center"><Package className="w-8 h-8 mx-auto mb-2 text-sage" />No products yet. Add one manually or upload your catalogue.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="brand-products-table">
            <thead className="bg-sand text-brown/70 text-xs uppercase tracking-wide">
              <tr><th className="px-2 py-2.5"></th><th className="text-left px-3 py-2.5">Product</th><th className="text-left px-3 py-2.5">SKU</th><th className="text-left px-3 py-2.5">Category</th><th className="text-right px-3 py-2.5">MRP</th><th className="text-left px-3 py-2.5">Status</th><th className="text-right px-3 py-2.5">Edit &amp; Share</th></tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t border-sage/15" data-testid={`brand-product-${p.id}`}>
                  <td className="px-2 py-2.5 text-center">{canEdit && canShare(p.status) && <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`select-product-${p.id}`} className="accent-forest w-4 h-4" />}</td>
                  <td className="px-3 py-2.5 text-forest font-500">{p.name}</td>
                  <td className="px-3 py-2.5 text-brown/70">{p.sku}</td>
                  <td className="px-3 py-2.5 text-brown/70">{p.category_name}</td>
                  <td className="px-3 py-2.5 text-right text-brown">₹{p.mrp}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-2.5 text-right"><div className="inline-flex gap-1.5">
                    {canShare(p.status) && <>
                      <button onClick={() => shareWhatsApp(p)} data-testid={`share-wa-${p.id}`} title="Share on WhatsApp" className="w-8 h-8 rounded-full border border-sage inline-flex items-center justify-center hover:bg-sage/10"><Share2 className="w-4 h-4 text-emerald-600" /></button>
                      <button onClick={() => copyLink(p)} data-testid={`copy-link-${p.id}`} title="Copy link" className="w-8 h-8 rounded-full border border-sage inline-flex items-center justify-center hover:bg-sage/10"><LinkIcon className="w-4 h-4 text-olive" /></button>
                    </>}
                    <button onClick={() => setImagesFor(p)} data-testid={`images-product-${p.id}`} title="Manage images" className="w-8 h-8 rounded-full border border-sage inline-flex items-center justify-center hover:bg-sage/10"><Images className="w-4 h-4 text-olive" /></button>
                    {canEdit && <button onClick={() => setEditing({ ...BLANK, ...p })} data-testid={`edit-product-${p.id}`} title="Edit" className="w-8 h-8 rounded-full border border-sage inline-flex items-center justify-center hover:bg-sage/10"><Pencil className="w-4 h-4 text-forest" /></button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={(o) => { if (!o) { setImportOpen(false); setValidation(null); setFile(null); } }}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Upload Catalogue</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <button onClick={downloadTemplate} className="text-sm text-olive hover:text-forest inline-flex items-center gap-1" data-testid="download-template-btn"><Download className="w-4 h-4" /> Download CSV template</button>
            <div onClick={() => fileRef.current?.click()} className="cursor-pointer rounded-xl border-2 border-dashed border-sage/50 bg-sand hover:bg-sage/10 px-4 py-6 text-center" data-testid="catalogue-dropzone">
              <FileSpreadsheet className="w-6 h-6 text-olive mx-auto mb-1" />
              <p className="text-sm text-brown font-500">{file ? file.name : "Click to choose a .csv or .xlsx file"}</p>
              <p className="text-xs text-brown/60">Required columns: name, sku, category, mrp</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setValidation(null); e.target.value = ""; }} />
            </div>

            {validation && (
              <div className="space-y-3" data-testid="validation-result">
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[["Rows", validation.summary.total_rows], ["New", validation.summary.new], ["Changed", validation.summary.changed], ["Discontinued", validation.summary.discontinued]].map(([l, v]) => (
                    <div key={l} className="bg-white rounded-xl py-2"><p className="text-lg font-heading text-forest">{v}</p><p className="text-[10px] text-brown/60">{l}</p></div>
                  ))}
                </div>
                {validation.errors.length > 0 ? (
                  <div className="bg-terracotta/10 border border-terracotta/30 rounded-xl p-3" data-testid="validation-errors">
                    <p className="text-sm font-500 text-terracotta flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4" /> {validation.errors.length} issue(s) — fix before importing</p>
                    <div className="max-h-44 overflow-y-auto space-y-1">
                      {validation.errors.map((e, i) => (
                        <p key={i} className="text-xs text-brown"><span className="font-600">Row {e.row}</span>{e.sku ? ` (${e.sku})` : ""}: {e.error}</p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-forest flex items-center gap-1.5 bg-sage/15 rounded-xl p-3"><CheckCircle2 className="w-4 h-4" /> No validation errors. Ready to import. Approved products with changes will be flagged for review, and missing SKUs will be marked inactive.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => runImport("validate")} disabled={busy || !file} className="rounded-full border-sage" data-testid="validate-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate"}</Button>
            <Button onClick={() => runImport("commit")} disabled={busy || !file || (validation && !validation.valid)} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="import-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import Now"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual add/edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{editing?.id ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              {editing.id && <div className="flex items-center gap-2 text-sm"><span className="text-brown/60">Current status:</span><StatusBadge status={editing.status} /></div>}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label className="text-sm mb-1 block">Product Name *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl bg-white" data-testid="pf-name" /></div>
                <div><Label className="text-sm mb-1 block">SKU {editing.id ? "" : "(auto if blank)"}</Label><Input value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} disabled={!!editing.id} className="rounded-xl bg-white" data-testid="pf-sku" /></div>
                <div><Label className="text-sm mb-1 block">Category *</Label><Input value={editing.category_name} onChange={(e) => setEditing({ ...editing, category_name: e.target.value })} className="rounded-xl bg-white" data-testid="pf-category" /></div>
                <div><Label className="text-sm mb-1 block">MRP *</Label><Input type="number" value={editing.mrp} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })} className="rounded-xl bg-white" data-testid="pf-mrp" /></div>
                <div><Label className="text-sm mb-1 block">Selling Price</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="rounded-xl bg-white" data-testid="pf-price" /></div>
                <div><Label className="text-sm mb-1 block">Pack Size</Label><Input value={editing.pack_size} onChange={(e) => setEditing({ ...editing, pack_size: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm mb-1 block">Stock</Label><Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="rounded-xl bg-white" /></div>
              </div>
              <div><Label className="text-sm mb-1 block">Short Description</Label><Textarea value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} rows={2} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm mb-1 block">Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="rounded-xl bg-white" /></div>
              <div><Label className="text-sm mb-1.5 block">Images</Label><GalleryUploader images={editing.images} onChange={(imgs) => setEditing((c) => ({ ...c, images: imgs }))} /></div>
              {editing.id && ["Approved", "Showcased", "Live"].includes(editing.status) && <p className="text-xs text-earth bg-mustard/15 rounded-lg p-2">This product is {editing.status}. Your edits will be submitted for review without changing the live version.</p>}
            </div>
          )}
          <DialogFooter><Button onClick={save} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-product-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing?.id ? "Save Changes" : "Add Product")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BrandProductImages product={imagesFor} open={!!imagesFor} onOpenChange={(o) => !o && setImagesFor(null)} onChanged={reloadAll} canEdit={canEdit} />
    </div>
  );
}
